import { HttpErrorResponse } from '@angular/common/http'
import { Component, Input, OnInit } from '@angular/core'
import { FormControl, Validators } from '@angular/forms'
import {
  AccountRunResponse,
  CommandService,
  CreateRestrictedActionRequest,
  RestrictedActionRequestTypes,
  RunFilteredAccountsPayload,
} from '@helios/commands'
import { ApprovalsKey } from '@helios/panels/approvals'
import {
  focusPanelAction,
  RootState,
  selectLegacyUserId,
  selectSelectedReconDate,
  setApprovalRequestSubmitted,
  showJobsAction,
} from '@helios/state-management'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalOptions,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { toISOFormat } from '@northfork/basics'
import { Daterange } from '@northfork/ng-basics'
import { ToastrService } from 'ngx-toastr'
import { Observable, Subject } from 'rxjs'
import { distinctUntilChanged, take, takeUntil, tap } from 'rxjs/operators'
import { ApprovalsRequestFailedAccountsComponent } from '../approvals-request-failed-accounts/approvals-request-failed-accounts.component'
import { BulkRunFilteredAccountsConfirmationModal } from '../bulk-run-filtered-accounts-confirmation/bulk-run-filtered-accounts-confirmation'
import { BulkRunNotAllowedWarningModal } from '../bulk-run-not-allowed-warning/bulk-run-not-allowed-warning-modal'
import { ForceReconTsWarningComponent } from '../force-recon-timestamp-warning/force-recon-ts-warning.component'

@Component({
  selector: 'app-bulk-run-filtered-accounts',
  templateUrl: './bulk-run-filtered-accounts.component.html',
  styleUrls: ['./bulk-run-filtered-accounts.component.scss'],
})
export class BulkRunFilteredAccountsComponent implements OnInit {
  @Input() filteredAccountIds?: string = ''
  dateRange: Daterange = {
    beginDate: null,
    endDate: null,
  }
  runType = new FormControl('CUSTODY_ETL')
  disableRunType = false
  runFrequency = new FormControl('EVERY_DAY', Validators.required)
  rePredictDistributions = new FormControl(false)
  forceReconTS = new FormControl(false)
  nDay = new FormControl(7)
  repeatingDay = new FormControl('First')

  private readonly _destroyed$ = new Subject<void>()

  Days = [
    'First',
    'Last',
    ...Array.from({ length: 30 }, (_, index) => {
      const number = index + 2
      const suffix =
        number % 10 === 1 && number !== 11
          ? 'st'
          : number % 10 === 2 && number !== 12
          ? 'nd'
          : number % 10 === 3 && number !== 13
          ? 'rd'
          : 'th'
      return `${number}${suffix}`
    }),
  ]
  reconDate?: Date
  userId?: number

  constructor(
    public activeModal: NgbActiveModal,
    private readonly store: Store<RootState>,
    private readonly modalService: NgbModal,
    private readonly commandService: CommandService,
    public toastService: ToastrService,
  ) {
    this.store
      .select(selectSelectedReconDate)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged(),
        tap(date => {
          if (date != null) {
            this.reconDate = date
            this.setLoadedReconDate(date)
          }
        }),
      )
      .subscribe()
    this.store
      .select(selectLegacyUserId)
      .pipe(take(1))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
  }
  ngOnInit(): void {
    this.forceReconTS.valueChanges.subscribe(value => {
      if (value) this.disableRunType = true
      else this.disableRunType = false
    })
  }

  setLoadedReconDate(reconDate: Date) {
    this.dateRange = {
      beginDate: toISOFormat(reconDate) + '',
      endDate: toISOFormat(reconDate) + '',
    }
  }

  applyDateRange(nfDateRange: Daterange) {
    this.dateRange = {
      beginDate: nfDateRange.beginDate,
      endDate: nfDateRange.endDate,
    }
  }

  async onSave() {
    const {
      filteredAccountIds,
      dateRange,
      forceReconTS,
      runType,
      modalService,
    } = this
    const dateRangeArray = this.getDatesInRange(dateRange)
    if (dateRangeArray.length === 0) {
      modalService.open(BulkRunNotAllowedWarningModal)
      return
    }
    if (!filteredAccountIds) return
    const numberOfRuns =
      filteredAccountIds.split(',').length * dateRangeArray.length
    if (forceReconTS.value) {
      const warningModalRef = modalService.open(ForceReconTsWarningComponent, {
        size: 'md',
        windowClass: 'modal-window-click-through',
        backdrop: false,
      } as NgbModalOptions)
      warningModalRef.result.then(
        justification => {
          this.runForceReconTS(
            dateRangeArray,
            filteredAccountIds,
            justification,
          )
        },
        () => {},
      )
      return
    }
    if (!numberOfRuns || numberOfRuns < 100) {
      this.runAccounts(dateRangeArray, filteredAccountIds, runType.value)
      return
    }
    const modalRef = modalService.open(
      BulkRunFilteredAccountsConfirmationModal,
      {
        size: 'md',
        windowClass: 'modal-window-click-through',
        backdrop: false,
      } as NgbModalOptions,
    )
    modalRef.componentInstance.numberOfRuns = numberOfRuns
    modalRef.result.then(
      confirmed => {
        if (confirmed) {
          this.runAccounts(dateRangeArray, filteredAccountIds, runType.value)
        }
      },
      () => {},
    )
  }

  runForceReconTS(
    reconDates: string[],
    accountIds: string,
    justification?: string,
  ) {
    const payloadAccountIds = accountIds?.split(',').map(Number)
    const payload: CreateRestrictedActionRequest = {
      actionType: RestrictedActionRequestTypes.FORCE_RECON,
      accountIds: payloadAccountIds,
      reconDates: reconDates,
      ...(justification && { comment: justification }),
    }
    this.runCommandService(
      this.commandService.runForceReconTS(payload),
      payloadAccountIds,
      'forceReconTS',
    )
  }

  runAccounts(reconDates: string[], accountIds: string, runType: string) {
    const payloadAccountIds = accountIds?.split(',').map(Number)
    const payload: RunFilteredAccountsPayload = {
      accountIds: payloadAccountIds,
      reconDates: reconDates,
      priority: 1,
      runEtls: runType !== 'CUSTODY',
      runType: runType === 'TRADING' ? 'TRADING' : 'CUSTODY',
      repredict: this.rePredictDistributions.value,
    }
    this.runCommandService(
      this.commandService.runFilteredAccounts(payload),
      payloadAccountIds,
      'bulkRunAccounts',
    )
  }

  runCommandService(
    serviceMethod: Observable<AccountRunResponse>,
    accountIds: number[],
    runType: 'forceReconTS' | 'bulkRunAccounts',
  ) {
    serviceMethod.subscribe({
      next: (response: AccountRunResponse) => {
        if (runType === 'forceReconTS') {
          if (response.invalidRequests && response.invalidRequests.length > 0) {
            this.toastService
              .error(
                'Some accounts failed the validation for Force Recon TS. Click here for details',
                '',
                {
                  closeButton: true,
                  timeOut: 30000, // 30 sec
                  extendedTimeOut: 10000, // 10 sec
                },
              )
              .onTap.subscribe(() => {
                const failedAccountsModal = this.modalService.open(
                  ApprovalsRequestFailedAccountsComponent,
                  {
                    size: 'lg',
                    windowClass: 'modal-window-click-through',
                    backdrop: false,
                    keyboard: false,
                  } as NgbModalOptions,
                )
                failedAccountsModal.componentInstance.failedAccounts =
                  response.invalidRequests
              })
          } else {
            this.toastService
              .success(
                'Request For Force Recon Timestamp Submitted',
                'Force Recon TS Submitted',
                { closeButton: true, timeOut: 30000, extendedTimeOut: 10000 },
              )
              .onTap.subscribe(() => {
                this.store.dispatch(focusPanelAction(ApprovalsKey))
              })
            this.store.dispatch(
              setApprovalRequestSubmitted({ approvalRequestSubmitted: true }),
            )
          }
        } else if (runType === 'bulkRunAccounts')
          this.toastService
            .success(
              'Click here to monitor submitted jobs in the Jobs panel.',
              `Bulk Run Submitted`,
              { closeButton: true, timeOut: 30000, extendedTimeOut: 10000 },
            )
            .onTap.subscribe(() => this.dispatchJobAction(response, accountIds))
      },
      error: (error: HttpErrorResponse) => this.handleHttpError(error),
    })
    this.activeModal.close()
  }

  dispatchJobAction(response: AccountRunResponse, accountIds: number[]) {
    this.store.dispatch(
      showJobsAction({
        userId: this.userId ?? 0,
        jobIds: response.jobIds ?? [],
        accountIds: accountIds,
        reconDate: toISOFormat(this.reconDate) ?? new Date().toISOString(),
      }),
    )
  }

  handleHttpError(error: HttpErrorResponse) {
    const message =
      error.status === 400
        ? 'Attempted to kick off too many runs. Max runs: 2000.'
        : 'Failed to submit runs. Please try again.'
    this.toastService.error(message, '', {
      closeButton: true,
      timeOut: 3000,
      disableTimeOut: false,
    })
  }

  // Function to get the dates between two dates in an array
  getDatesInRange(dateRange: Daterange): string[] {
    return this.getDateRangeBasedOnFrequency(dateRange)
  }
  getDateRangeBasedOnFrequency(dateRange: Daterange): string[] {
    switch (this.runFrequency.value) {
      case 'EVERY_DAY':
        return this.getDateRangesForEveryNDays(dateRange, 1)
      case 'EVERY_N_DAYS':
        return this.getDateRangesForEveryNDays(
          dateRange,
          Number(this.nDay.value),
        )
      case 'EVERY_NTH_DAY_OF_THE_MONTH':
        return this.getDateRangesForEveryNthDay(dateRange)
    }
    return []
  }

  getDateRangesForEveryNDays(dateRange: Daterange, frequency: number) {
    if (!dateRange.beginDate || !dateRange.endDate) return []
    const dateRangeArray = []
    const currentDate = new Date(dateRange.beginDate)
    while (currentDate <= new Date(dateRange.endDate)) {
      dateRangeArray.push(currentDate.toISOString().split('T')[0])
      currentDate.setDate(currentDate.getDate() + frequency)
    }
    return dateRangeArray
  }

  getDateRangesForEveryNthDay(dateRange: Daterange) {
    if (!dateRange.beginDate || !dateRange.endDate) return []

    const dateRangeArray = []
    const dayOfMonth = this.repeatingDay.value
    const currentDate = new Date(dateRange.beginDate)
    const endDate = new Date(dateRange.endDate)

    while (currentDate <= endDate) {
      const currentDay = currentDate.getUTCDate()

      if (
        (dayOfMonth === 'First' && currentDay === 1) ||
        (dayOfMonth === 'Last' &&
          currentDay === this.getLastDayOfCurrentMonth(currentDate)) ||
        parseInt(dayOfMonth) === currentDay
      ) {
        dateRangeArray.push(currentDate.toISOString().split('T')[0])
      }
      currentDate.setUTCDate(currentDate.getUTCDate() + 1)
    }
    return dateRangeArray
  }

  private getLastDayOfCurrentMonth(currentDate: Date): number {
    return new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
    ).getDate()
  }
}
